import React, { useContext, useEffect, useState } from 'react';
import DailyRecapModal from './DailyRecapModal';
import { Layout as AntLayout, Avatar, Dropdown, Space, Typography, Button, Alert, Tooltip } from 'antd';
import { UserOutlined, LogoutOutlined, SearchOutlined, BarChartOutlined, TeamOutlined, FileDoneOutlined, FormOutlined, LineChartOutlined, PieChartOutlined, IdcardOutlined, PhoneOutlined, BellOutlined, ExperimentOutlined, TableOutlined, BookOutlined, UnorderedListOutlined, ProfileOutlined, SwapOutlined, MessageOutlined, AuditOutlined, TrophyOutlined, RobotOutlined, CalendarOutlined, SafetyOutlined, WarningOutlined, StopOutlined, ClockCircleOutlined, ScheduleOutlined, FundProjectionScreenOutlined, SolutionOutlined, HistoryOutlined, LikeOutlined, LockOutlined, MailOutlined, CustomerServiceOutlined, UploadOutlined, BulbOutlined, ContactsOutlined, SettingOutlined, ApartmentOutlined, MoonOutlined, SunOutlined, NotificationOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { UserContext, ThemeModeContext, hasGrant } from '../App';
import { clearGoogleCredential } from '../api';
import AlertBell from './AlertBell';
import MockUserSwitcher from './MockUserSwitcher';
import QuickCreate from './QuickCreate';
import useNewVersion from './useNewVersion';
import HarborNav, { useNavStyle } from './HarborNav/HarborNav';
import ClassicNav from './HarborNav/ClassicNav';
import ProfileModal from './HarborNav/ProfileModal';
import HarborTheme, { HARBOR_PAGES } from './HarborNav/HarborPageTheme';

const MOCK_MODE = process.env.REACT_APP_MOCK_MODE === 'true';

const { Header, Content } = AntLayout;

import { NavTrailProvider, BackChip, RecentMenu } from './BackChip';

export default function Layout({ children }) {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  // Why the user was sent somewhere they did not ask to go. 'denied' rides on
  // the router state from Guard, so it clears on the next navigation; 'session'
  // is left in sessionStorage by the 401 handler before the page reloads.
  const [sessionNotice, setSessionNotice] = useState(false);
  // A newer Harbor has been deployed since this tab loaded.
  const newVersion = useNewVersion();
  useEffect(() => {
    try {
      if (sessionStorage.getItem('harborNotice') === 'session') {
        sessionStorage.removeItem('harborNotice');
        setSessionNotice(true);
      }
    } catch {}
  }, []);
  const deniedNotice = location.state?.notice === 'denied';
  const currentUser = useContext(UserContext);
  const { dark, setDark } = useContext(ThemeModeContext);
  // The Harbor redesign is opt-in per browser; classic stays the default.
  const [navStyle, setNavStyle] = useNavStyle();
  const [profileOpen, setProfileOpen] = useState(false); // here, not in HarborNav: it stays open while Classic is previewed
  const harbor = navStyle === 'harbor';
  const [impersonateName] = useState(() => localStorage.getItem('impersonateUserName'));

  const stopImpersonating = () => {
    localStorage.removeItem('impersonateUserId');
    localStorage.removeItem('impersonateUserName');
    window.location.href = '/';
  };

  const handleLogout = async () => {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith('qa_filter_options'))
      .forEach((k) => sessionStorage.removeItem(k));
    clearGoogleCredential();
    await instance.clearCache();
    window.location.replace(window.location.origin);
  };

  const role = currentUser?.Role;
  const isAgent        = role === 'agent';
  // Agents and support agents together, matching how the agent panel and the
  // leaderboard already treat the pair.
  const isAgentLike    = ['agent', 'support_agent'].includes(role);
  const isQaAdmin      = role === 'qa_admin';
  const isAdmin        = role === 'admin';
  const isQa           = role === 'qa';

  // Scalar permission values
  const canEditEvals          = currentUser?.CanEditEvals ?? 0;
  const canEditErrors         = currentUser?.CanEditErrors ?? 0;
  const canEditCoaching       = currentUser?.CanEditCoaching ?? 0;
  const canEditIncidents      = currentUser?.CanEditIncidents ?? 0;
  const canEditFormalWriteups = currentUser?.CanEditFormalWriteups ?? 0;
  const canEditAbsences       = currentUser?.CanEditAbsences ?? 0;
  const canEditScheduleVariance = currentUser?.CanEditScheduleVariance ?? 0;
  // The Help Desk search page: CanEditHelpDesk, queue members, "Who can
  // submit" members -- worked out by the server.
  const canEditHelpDesk       = currentUser?.HelpDeskSearch ? 1 : 0;
  const canEditClients        = currentUser?.CanEditClients ?? 0;

  // Boolean flag shortcuts
  const canEditStaffHistory = (currentUser?.CanEditStaffHistory ?? 0) >= 1;
  const canEditUsers        = (currentUser?.CanEditUsers ?? 0) >= 1;
  const canConfigEvals      = (currentUser?.CanConfigEvals ?? 0) >= 1;
  const canViewCampaign     = (currentUser?.CanViewCampaignAnalytics ?? 0) >= 1;
  const canViewAI           = (currentUser?.CanViewAIAnalytics ?? 0) >= 1;
  const canViewOversight    = (currentUser?.CanViewOversightStats ?? 0) >= 1;
  const canViewRecruitment   = (currentUser?.CanViewRecruitmentStats ?? 0) >= 1;
  const canEditForecasting   = (currentUser?.CanEditForecasting ?? 0) >= 1;
  const canSearchCalls       = (currentUser?.CanSearchCalls ?? 0) >= 1;
  const canViewAgentSummary  = (currentUser?.CanViewAgentSummary ?? 0) >= 1;
  const canEditEngagements   = (currentUser?.CanEditEngagements ?? 0) >= 1;
  const isCneAuth            = isAdmin || isQaAdmin || (currentUser?.JobID === 6 && currentUser?.TeamID === 3);
  const isConHomeUpsellAuth  = isAdmin || isQaAdmin || (currentUser?.JobID === 6 && currentUser?.TeamID === 1);

  const canViewCNEStats     = canViewCampaign && (!(currentUser?.VisibleTeams?.length) || currentUser.VisibleTeams.some(t => t.ID === 3));
  const canViewUpsellStats  = (isAgent && currentUser?.TeamID === 1) || (!isAgent);

  const interactionPaths = ['/search', '/analytics', '/campaign-stats', '/erlang-stats', '/campaign-dispo-stats', '/campaign-stats-today', '/campaign-agent-stats', '/cne-monthly-stats', '/cne-daily-stats', '/campaign-weekly-scorecard'];
  const evalPaths        = ['/cases', '/eval-insights', '/eval-analytics', '/cxone-scorecard-stats', '/eval-agent-stats', '/eval-ai-audit', '/eval-evaluator-stats', '/eval-progress', '/calibration', '/eval-raw-data', '/eval-qa-comparison', '/feedback-only', '/my-evals', '/errors', '/error-agent-stats', '/error-evaluator-stats', '/error-analytics', '/error-insights', '/error-raw-data', '/my-errors', '/compliance/incidents', '/incident-stats'];
  // Grants for Admin pages open the Admin menu.
  const hasAnyGrant      = (currentUser?.PageGrants || []).length > 0;
  // Projects: Projects Access View or Edit (or admin); never agents.
  const canUseProjects   = !isAgent && (currentUser?.Role === 'admin' || (currentUser?.CanEditProjects ?? 0) >= 1);
  const canCampaignUpdates = currentUser?.Role === 'admin' || (currentUser?.CanEditCampaignUpdates ?? 0) >= 1;
  // WFM Skills Access (CanEditWFMSkills): 1 View and up assign; the config
  // page is Edit Manager (3). Admin needs no grant; qa_admin does.
  const canWfmSkills       = currentUser?.Role === 'admin' || (currentUser?.CanEditWFMSkills ?? 0) >= 1;
  const canWfmSkillsConfig = currentUser?.Role === 'admin' || (currentUser?.CanEditWFMSkills ?? 0) >= 3;
  // Shift Swap Forum menu entry: WFM Skills 3 (from /api/me); the page itself is open by URL.
  const canSwapForum       = !!currentUser?.ShiftSwapForum;
  const adminPaths       = ['/admin', '/eval-forms', '/admin/alert-config', '/admin/coaching-config', '/admin/sidekick-config', '/admin/campaigns', '/admin/teams', '/admin/compliance-config', '/admin/skill-exclusions', '/admin/skill-groups', '/admin/wfm-skills', '/admin/shift-department-eligibility', '/admin/grade-weights', '/admin/ccaas-statuses', '/admin/badges', '/admin/recap-templates', '/admin/helpdesk-queues', '/admin/helpdesk-automations', '/admin/helpdesk-workflows', '/admin/clients', '/admin/cne-upload', '/admin/cne-daily-upload', '/admin/cne-login-upload', '/admin/cne-qa-upload', '/admin/cne-csat-upload', '/admin/dbal-thresholds', '/admin/employee-jobs', '/admin/project-types', '/admin/campaign-updates-config', '/admin/goals', '/admin/eval-criteria', '/admin/cheers-config', '/admin/lock-dates', '/admin/email-config', '/admin/principal-bill', '/admin/bcbs-bill', '/admin/payroll-gl', '/admin/conhome-upsell-import', '/admin/stars-adherence-upload', '/admin/campaign-updates-upload'];
  const isAdminEngagementPath = location.pathname.startsWith('/admin/engagements');
  const isMyEngagementPath    = location.pathname.startsWith('/my-engagements');
  const isIdeaPath            = location.pathname === '/ideas' || location.pathname.startsWith('/ideas/');
  const oversightPaths   = ['/agents', '/campaign-summary', '/absences', '/schedule-variances', '/compliance/writeups', '/dbal', '/compliance/adherence', '/compliance/team-timeline', '/compliance/team-ccaas-stats', '/scheduled-staffing', '/notifications', '/projects', '/project-tasks', '/campaign-updates', '/wfm-skills', '/availability', '/shift-board', '/break-lunch-schedule', '/shift-swap-forum', '/shift-swap-forum/explore'];
  const recruitPaths     = ['/census', '/fte-forecast', '/recruitment-stats', '/attrition', '/headcount', '/compliance/timecard-summary', '/admin/employee-history', '/cost-revenue'];
  // Cost vs Revenue and Payroll GL. Computed server-side from the single rule
  // in auth.py -- an allowlisted user holding Executive financial access.
  // Admin is neither required nor sufficient, so do not re-derive it here.
  const canViewCostRevenue = !!currentUser?.CanViewPayrollFinancials;
  const coachingPaths    = ['/coaching/sessions', '/coaching/agent-stats', '/coaching/coach-stats', '/coaching/my-sessions', '/coaching/generate-recap'];
  const aiPaths          = ['/sidekick'];
  const agentSummaryPaths = ['/agent-summary', '/my-absences', '/my-campaign-updates', '/my-helpdesk', '/compliance/my-issues', '/coaching/my-sessions', '/my-evals', '/my-errors', '/wall-of-fame', '/cheers', '/my-engagements', '/ideas'];

  const selectedKeys = [
    location.pathname,
    ...(location.pathname === '/leaderboard' ? ['/leaderboard'] : []),
    ...(interactionPaths.includes(location.pathname) ? ['interactions-group'] : []),
    ...(evalPaths.some((p) => location.pathname === p || location.pathname.startsWith('/calibration') || location.pathname.startsWith('/errors/')) ? ['evaluations-group'] : []),
    ...(adminPaths.includes(location.pathname) || location.pathname.startsWith('/admin/clients/') || location.pathname.startsWith('/admin/helpdesk-automations/') || location.pathname.startsWith('/admin/helpdesk-workflows/') || location.pathname.startsWith('/eval-forms/') ? ['admin-group'] : []),
    ...(isAdminEngagementPath ? ['oversight-group'] : []),
    // Admin's second level: the group the page sits in.
    ...(['/admin', '/admin/sidekick-config', '/admin/alert-config', '/admin/campaigns', '/admin/email-config', '/admin/employee-jobs', '/admin/project-types', '/admin/campaign-updates-config',
         '/admin/grade-weights', '/admin/helpdesk-queues', '/admin/teams'].includes(location.pathname)
      || location.pathname.startsWith('/admin/helpdesk-automations') || location.pathname.startsWith('/admin/helpdesk-workflows') ? ['admin-config'] : []),
    ...(['/admin/badges', '/admin/goals', '/admin/ccaas-statuses', '/admin/cheers-config', '/admin/clients', '/admin/coaching-config',
         '/admin/compliance-config', '/admin/dbal-thresholds', '/admin/lock-dates', '/admin/skill-exclusions',
         '/admin/skill-groups', '/admin/wfm-skills', '/admin/shift-department-eligibility'].includes(location.pathname)
      || location.pathname.startsWith('/admin/clients/') ? ['admin-business'] : []),
    ...(['/admin/eval-criteria', '/eval-forms', '/admin/recap-templates'].includes(location.pathname)
      || location.pathname.startsWith('/eval-forms/') ? ['admin-forms'] : []),
    ...(location.pathname.endsWith('-upload') || ['/admin/bcbs-bill', '/admin/payroll-gl', '/admin/principal-bill', '/admin/conhome-upsell-import'].includes(location.pathname)
      ? ['admin-uploads'] : []),
    ...(location.pathname.startsWith('/helpdesk') ? ['/helpdesk'] : []),
    ...(coachingPaths.some((p) => location.pathname.startsWith(p)) ? ['evaluations-group'] : []),
    // Evaluate & Coach's second level: the group the page sits in.
    ...(coachingPaths.some((p) => location.pathname.startsWith(p)) || location.pathname.startsWith('/calibration')
      ? ['evaluations-coaching'] : []),
    ...(location.pathname.startsWith('/compliance/incidents') || location.pathname === '/incident-stats'
      ? ['evaluations-compliance'] : []),
    ...(location.pathname.startsWith('/errors') || location.pathname.startsWith('/error-')
      ? ['evaluations-errors'] : []),
    ...(['/cases', '/feedback-only', '/eval-analytics', '/eval-agent-stats', '/eval-insights', '/eval-progress', '/eval-evaluator-stats',
         '/eval-qa-comparison', '/eval-ai-audit', '/eval-raw-data', '/cxone-scorecard-stats'].includes(location.pathname)
      || location.pathname.startsWith('/cases/')
      ? ['evaluations-evals'] : []),
    ...(aiPaths.includes(location.pathname) ? ['ai-group'] : []),
    ...(oversightPaths.some((p) => location.pathname === p || location.pathname.startsWith('/compliance/')) && !location.pathname.startsWith('/compliance/incidents') && !location.pathname.startsWith('/compliance/my-issues') && !location.pathname.startsWith('/compliance/timecard') ? ['oversight-group'] : []),
    ...(location.pathname.startsWith('/folders/') ? ['/projects'] : []),
    // Oversight's sub-menus: the one the page sits in.
    ...(['/absences', '/schedule-variances', '/compliance/adherence', '/dbal', '/compliance/team-timeline',
         '/compliance/team-ccaas-stats', '/scheduled-staffing', '/wfm-skills', '/availability', '/shift-board', '/break-lunch-schedule',
         '/shift-swap-forum', '/shift-swap-forum/explore'].includes(location.pathname)
      || location.pathname.startsWith('/absences/') ? ['oversight-attendance'] : []),
    ...(['/projects', '/project-tasks', '/campaign-updates'].includes(location.pathname) || location.pathname.startsWith('/folders/')
      ? ['oversight-projects'] : []),
    ...(location.pathname.startsWith('/helpdesk') ? ['oversight-helpdesk'] : []),
    ...(oversightPaths.includes(location.pathname) || location.pathname.startsWith('/helpdesk')
      || location.pathname.startsWith('/folders/') ? ['oversight-group'] : []),
    ...(recruitPaths.includes(location.pathname) ? ['recruit-group'] : []),
    ...(agentSummaryPaths.includes(location.pathname) || isMyEngagementPath || isIdeaPath ? ['agent-summary-group'] : []),
  ];

  // A client's own login: Campaign Summary, and with their permissions
  // Campaign Updates, Agent Summary, WFM Skills Assign,
  // Search, Campaign Stats and Campaign Stats Today (App.js routes them).
  // Their own menu groups; a group with nothing in it is left out.
  const clientNavItems = [
    { key: 'client-campaign-group', label: 'Campaign', icon: <FundProjectionScreenOutlined />, children: [
      { key: '/campaign-summary', label: 'Campaign Summary', icon: <LineChartOutlined /> },
      ...(canCampaignUpdates ? [{ key: '/campaign-updates', label: 'Campaign Updates', icon: <NotificationOutlined /> }] : []),
    ] },
    { key: 'client-agents-group', label: 'Agents', icon: <TeamOutlined />, children: [
      ...(canViewAgentSummary ? [{ key: '/agents', label: 'Agent Summary', icon: <SearchOutlined /> }] : []),
      ...((currentUser?.CanEditWFMSkills ?? 0) >= 1 ? [{ key: '/wfm-skills', label: 'WFM Skills Assign', icon: <IdcardOutlined /> }] : []),
    ] },
    { key: 'client-interactions-group', label: 'Interactions', icon: <PhoneOutlined />, children: [
      ...(canSearchCalls ? [{ key: '/search', label: 'Search', icon: <SearchOutlined /> }] : []),
      ...(canViewCampaign ? [
        { key: '/campaign-stats', label: 'Campaign Stats', icon: <BarChartOutlined /> },
        { key: '/campaign-stats-today', label: 'Campaign Stats Today', icon: <BarChartOutlined /> },
      ] : []),
    ] },
    { key: 'client-helpdesk-group', label: 'HelpDesk', icon: <CustomerServiceOutlined />, children: [
      { key: '/my-helpdesk', label: 'My Help Desk', icon: <CustomerServiceOutlined /> },
    ] },
  ].filter((g) => g.children.length);

  const navItems = role === 'client' ? clientNavItems : [
    {
      key: 'agent-summary-group',
      label: 'My Hub',
      icon: <IdcardOutlined />,
      children: [
        { key: '/agent-summary',        label: 'My Summary',               icon: <LineChartOutlined /> },
        { key: '/my-evals',             label: 'My Evaluations',           icon: <AuditOutlined /> },
        { key: '/my-errors',            label: 'My Errors',                icon: <AuditOutlined /> },
        { key: '/compliance/my-issues', label: 'My Compliance Issues',     icon: <SafetyOutlined /> },
        { key: '/coaching/my-sessions', label: 'My Coaching Sessions',     icon: <ProfileOutlined /> },
        { key: '/my-engagements',       label: 'My Engagements & Contests',  icon: <TrophyOutlined /> },
        { key: '/my-absences',          label: 'My Absence Notifications', icon: <CalendarOutlined /> },
        { key: '/my-campaign-updates',  label: 'My Campaign Updates',      icon: <NotificationOutlined /> },
        { key: '/my-helpdesk',          label: 'My HelpDesk',              icon: <CustomerServiceOutlined /> },
        { key: '/cheers',               label: 'Cheers for Peers',         icon: <LikeOutlined /> },
        ...(canViewUpsellStats ? [{ key: '/upsell-stats', label: 'Upsell Board', icon: <LineChartOutlined /> }] : []),
        { key: '/wall-of-fame',         label: 'Wall of Fame',             icon: <TrophyOutlined /> },
        // Everyone, agents included (opened to agents 2026-10-21).
        { key: '/ideas', label: 'Submit Harbor Idea', icon: <BulbOutlined /> },
      ],
    },

    // Interactions — search + campaign analytics + AI analytics
    ...((canViewCampaign || canViewAI || canEditEvals >= 1 || canSearchCalls) ? [{
      key: 'interactions-group',
      label: 'Interactions',
      icon: <PhoneOutlined />,
      children: [
        ...(canEditEvals >= 1 || canSearchCalls ? [{ key: '/search', label: 'Search', icon: <SearchOutlined /> }] : []),
        ...(canViewCampaign ? [
          { key: '/campaign-stats',             label: 'Campaign Stats',          icon: <LineChartOutlined /> },
          { key: '/campaign-stats-today',       label: 'Campaign Stats Today',    icon: <LineChartOutlined /> },
          { key: '/campaign-weekly-scorecard',  label: 'Campaign Weekly Scorecard', icon: <LineChartOutlined /> },

          { key: '/campaign-dispo-stats', label: 'Campaign Dispo Stats',  icon: <PieChartOutlined /> },
          { key: '/campaign-agent-stats', label: 'Campaign Agent Stats', icon: <TeamOutlined /> },
          { key: '/erlang-stats',         label: 'Campaign Erlang Stats', icon: <LineChartOutlined /> },
        ] : []),
        ...(canViewCNEStats ? [
          { key: '/cne-monthly-stats', label: 'CNE Monthly Stats', icon: <BarChartOutlined /> },
          { key: '/cne-daily-stats',   label: 'CNE Daily Stats',   icon: <BarChartOutlined /> },
        ] : []),
        ...(canViewAI ? [
          { key: '/analytics', label: 'AI Analytics', icon: <BarChartOutlined /> },
        ] : []),
      ],
    }] : []),

    // Evaluate & Coach — flag-gated per tier
    ...((canEditEvals >= 1 || canEditErrors >= 1 || canEditCoaching >= 1 || canEditIncidents >= 1) ? [{
      key: 'evaluations-group',
      label: 'Evaluate & Coach',
      icon: <FileDoneOutlined />,
      // Second level: Coaching, Evaluations, Errors, Compliance. Each item
      // keeps its own rule; a group shows only when something in it does.
      children: [
        {
          key: 'evaluations-coaching',
          label: 'Coaching',
          icon: <BookOutlined />,
          children: [
            ...(canEditCoaching >= 1 ? [
              { key: '/coaching/sessions',    label: 'Coaching Sessions',    icon: <UnorderedListOutlined /> },
              { key: '/coaching/agent-stats', label: 'Coaching Agent Stats', icon: <BarChartOutlined /> },
              { key: '/coaching/coach-stats', label: 'Coaching Coach Stats', icon: <BarChartOutlined /> },
            ] : []),
            // A limited group to start: CanEditEvals >= 3 and CanEditCoaching
            // >= 2 (enough to create the sessions). Same rule as the page and
            // the server; no role exception.
            ...(canEditEvals >= 3 && canEditCoaching >= 2 ? [
              { key: '/coaching/generate-recap', label: 'Generate Coaching Recap', icon: <FormOutlined /> },
            ] : []),
            // Calibration is coaching work, so it sits here, though it needs
            // CanEditEvals >= 2 rather than a coaching flag.
            ...(canEditEvals >= 2 ? [{ key: '/calibration', label: 'Calibration', icon: <ExperimentOutlined /> }] : []),
          ],
        },
        {
          key: 'evaluations-evals',
          label: 'Evaluations',
          icon: <FileDoneOutlined />,
          children: [
            // Evals tier 1
            ...(canEditEvals >= 1 ? [
              { key: '/cases',            label: 'Eval Search',              icon: <SearchOutlined /> },
              { key: '/feedback-only',    label: 'Eval FeedbackOnly Search', icon: <SearchOutlined /> },
              { key: '/eval-analytics',   label: 'Eval Analytics',           icon: <PieChartOutlined /> },
              { key: '/eval-agent-stats', label: 'Eval Agent Stats',         icon: <LineChartOutlined /> },
              { key: '/eval-insights',    label: 'Eval Insights',            icon: <BulbOutlined /> },
              { key: '/eval-progress',    label: 'Eval Progress',            icon: <AuditOutlined /> },
            ] : []),
            // Evals tier 2
            ...(canEditEvals >= 2 ? [
              { key: '/eval-evaluator-stats', label: 'Eval Evaluator Stats', icon: <TeamOutlined /> },
              { key: '/eval-qa-comparison',   label: 'Eval Evaluator Comp',  icon: <SwapOutlined /> },
            ] : []),
            // Admin only while the AI scoring is being proven. It is a review of
            // how the model is behaving rather than of anyone's work, and it is
            // not useful to read until someone is acting on what it shows.
            ...(['admin', 'qa_admin'].includes(role) ? [
              { key: '/eval-ai-audit',        label: 'Eval AI Score Audit',  icon: <AuditOutlined /> },
            ] : []),
            // Evals tier 3
            ...(canEditEvals >= 3 ? [
              { key: '/eval-raw-data', label: 'Eval Data Export', icon: <TableOutlined /> },
            ] : []),
            // CXOne Scorecard Stats below Eval Data Export
            ...(canEditEvals >= 1 ? [{ key: '/cxone-scorecard-stats', label: 'CXOne Scorecard Stats', icon: <BarChartOutlined /> }] : []),
          ],
        },
        {
          key: 'evaluations-errors',
          label: 'Errors',
          icon: <AuditOutlined />,
          children: [
            // Errors tier 1
            ...(canEditErrors >= 1 ? [
              { key: '/errors',           label: 'Error Search',      icon: <AuditOutlined /> },
              { key: '/error-analytics',  label: 'Error Analytics',   icon: <BarChartOutlined /> },
              { key: '/error-agent-stats',label: 'Error Agent Stats', icon: <LineChartOutlined /> },
              { key: '/error-insights',   label: 'Error Insights',    icon: <BulbOutlined /> },
            ] : []),
            // Errors tier 2
            ...(canEditErrors >= 2 ? [
              { key: '/error-evaluator-stats', label: 'Error Evaluator Stats', icon: <TeamOutlined /> },
            ] : []),
            // Errors tier 3
            ...(canEditErrors >= 3 ? [
              { key: '/error-raw-data', label: 'Error Data Export', icon: <TableOutlined /> },
            ] : []),
          ],
        },
        {
          key: 'evaluations-compliance',
          label: 'Compliance',
          icon: <SafetyOutlined />,
          children: [
            ...(canEditIncidents >= 1 ? [{ key: '/compliance/incidents', label: 'Compliance Incidents', icon: <WarningOutlined /> }] : []),
            ...(canEditIncidents >= 1 ? [{ key: '/incident-stats', label: 'Compliance Stats', icon: <BarChartOutlined /> }] : []),
          ],
        },
      ].filter((group) => group.children.length),
    }] : []),

    // Oversight
    // Engagements sits here but keeps its own flag, with no agent check, as it had under Admin.
    // !isAgentLike: Schedule Break & Lunch (Attendance & Schedule) is every non-agent's.
    ...((!isAgent && (canViewAgentSummary || canViewCampaign || canEditAbsences >= 1 || canEditScheduleVariance >= 1 || canEditHelpDesk >= 1 || canEditIncidents >= 1 || canEditFormalWriteups >= 1 || canViewOversight || canUseProjects || canWfmSkills)) || canEditEngagements || canCampaignUpdates || !isAgentLike || canSwapForum ? [{
      key: 'oversight-group',
      label: 'Oversight',
      icon: <SafetyOutlined />,
      // Two sub-menus (Attendance & Schedule, Projects & Tasks); the rest sit
      // directly under Oversight. A sub-menu shows only when something in it
      // does. Alphabetical at each level, so a page is easy to find whatever
      // the user can see.
      children: [
        ...(canViewAgentSummary ? [{ key: '/agents', label: 'Agent Summary', icon: <SearchOutlined /> }] : []),
        ...(canViewCampaign ? [{ key: '/campaign-summary', label: 'Campaign Summary', icon: <LineChartOutlined /> }] : []),
        ...(canEditIncidents >= 1 ? [{ key: '/notifications', label: 'AI Notifications', icon: <BellOutlined /> }] : []),
        ...(canEditFormalWriteups >= 1 ? [{ key: '/compliance/writeups', label: 'Formal Writeups', icon: <AuditOutlined /> }] : []),
        ...(canEditEngagements ? [{ key: '/admin/engagements', label: 'Engagements', icon: <TrophyOutlined /> }] : []),
        {
          key: 'oversight-attendance',
          label: 'Attendance & Schedule',
          icon: <CalendarOutlined />,
          children: [
            ...(canEditAbsences >= 1 ? [{ key: '/absences', label: 'Absence Notifications', icon: <CalendarOutlined /> }] : []),
            ...(canEditScheduleVariance >= 1 ? [{ key: '/schedule-variances', label: 'Schedule Variance Log', icon: <ScheduleOutlined /> }] : []),
            ...(canWfmSkills ? [{ key: '/wfm-skills', label: 'WFM Skills Assign', icon: <IdcardOutlined /> }] : []),
            ...(canWfmSkills ? [{ key: '/availability', label: 'WFM Availability', icon: <ScheduleOutlined /> }] : []),
            // Shift Board phase 1: admin only while it is tested.
            ...(isAdmin || canEditAbsences >= 3 ? [{ key: '/shift-board', label: 'WFM Absence Swap List', icon: <SwapOutlined /> }] : []),
            ...(canSwapForum ? [{ key: '/shift-swap-forum', label: 'Shift Swap Forum', icon: <SwapOutlined /> }] : []),
            // Moved from My Hub; still every non-agent's.
            ...(!isAgentLike ? [{ key: '/break-lunch-schedule', label: 'Schedule Break & Lunch', icon: <ScheduleOutlined /> }] : []),
            ...(canViewOversight ? [
              { key: '/compliance/adherence',   label: 'Adherence',    icon: <BarChartOutlined /> },
              { key: '/dbal',                   label: 'DBAL',         icon: <ScheduleOutlined /> },
              { key: '/compliance/team-timeline',    label: 'Team Timeline',    icon: <ClockCircleOutlined /> },
              { key: '/compliance/team-ccaas-stats', label: 'Team Max CCaaS Stats', icon: <BarChartOutlined /> },
              { key: '/scheduled-staffing',          label: 'Scheduled Staff By Hour', icon: <ScheduleOutlined /> },
            ] : []),
          ].sort((x, y) => x.label.localeCompare(y.label)),
        },
        {
          // Its own sub-menu, for the Help Desk pages to come.
          key: 'oversight-helpdesk',
          label: 'Help Desk',
          icon: <CustomerServiceOutlined />,
          children: [
            // The Help Desk search (all tickets this user may see). Agents'
            // own tickets are on My Hub > My HelpDesk.
            ...(canEditHelpDesk >= 1 ? [{ key: '/helpdesk', label: 'Help Desk Search', icon: <CustomerServiceOutlined /> }] : []),
          ],
        },
        {
          key: 'oversight-projects',
          label: 'Projects & Tasks',
          icon: <ApartmentOutlined />,
          children: [
            ...(canUseProjects ? [
              { key: '/projects',      label: 'Projects',      icon: <ApartmentOutlined /> },
              { key: '/project-tasks', label: 'Project Tasks', icon: <UnorderedListOutlined /> },
            ] : []),
            ...(canCampaignUpdates ? [{ key: '/campaign-updates', label: 'Campaign Updates', icon: <NotificationOutlined /> }] : []),
          ],
        },
      ].filter((x) => !x.children || x.children.length)
        .sort((x, y) => x.label.localeCompare(y.label)),
    }] : []),

    // Recruit & Forecast
    ...((canEditStaffHistory || canViewCostRevenue
         || (!isAgent && (canViewRecruitment || canEditForecasting))) ? [{
      key: 'recruit-group',
      label: 'Recruit & Forecast',
      icon: <FundProjectionScreenOutlined />,
      children: [
        ...(canEditStaffHistory ? [{ key: '/admin/employee-history', label: 'Employee History', icon: <HistoryOutlined /> }] : []),
        ...(canViewRecruitment ? [
          { key: '/census',             label: 'Employee Census',    icon: <BarChartOutlined /> },
          { key: '/recruitment-stats',  label: 'Recruitment Stats',  icon: <LineChartOutlined /> },
          { key: '/attrition',          label: 'Attrition',          icon: <LineChartOutlined /> },
          { key: '/headcount',          label: 'Headcount History',  icon: <TeamOutlined /> },
        ] : []),
        ...(canEditForecasting ? [
          { key: '/fte-forecast',                  label: 'FTE Forecasting',   icon: <FundProjectionScreenOutlined /> },
          { key: '/compliance/timecard-summary',   label: 'Time Card Summary', icon: <TableOutlined /> },
        ] : []),
        ...(canViewCostRevenue ? [
          { key: '/cost-revenue',                  label: 'Cost vs Revenue',   icon: <BarChartOutlined /> },
        ] : []),
      ],
    }] : []),

    // AI Sidekick
    ...(isAdmin ? [{
      key: 'ai-group',
      label: 'AI Sidekick',
      icon: <RobotOutlined />,
      children: [
        { key: '/sidekick', label: 'Sidekick Chat', icon: <RobotOutlined /> },
      ],
    }] : []),

    // Everyone but agents, who need LEADERBOARD_AGENTS_ENABLED on first.
    ...(!isAgent || !!currentUser?.LeaderboardAgentsEnabled
      ? [{ key: '/leaderboard', label: 'Leaderboard', icon: <TrophyOutlined /> }] : []),

    // Admin
    // A page grant has to open the parent group too, or the item it unlocks is
    // unreachable from the nav.
    ...(isAdmin || isQaAdmin || isCneAuth || canEditUsers || canEditClients >= 1 || canWfmSkillsConfig || hasAnyGrant ? [{
      key: 'admin-group',
      label: 'Admin',
      icon: <TeamOutlined />,
      // Second level: Admin Config, Business Config, Forms & Templates, File
      // Uploads. Each item keeps its own rule; a group shows only when
      // something in it does.
      children: [
        {
          key: 'admin-config',
          label: 'Admin Config',
          icon: <SettingOutlined />,
          children: [
            ...(isAdmin || isQaAdmin ? [
              { key: '/admin/sidekick-config',    label: 'AI Sidekick Config', icon: <RobotOutlined /> },
              { key: '/admin/alert-config',       label: 'Alert Config',      icon: <BellOutlined /> },
              { key: '/admin/campaigns',          label: 'Campaigns',         icon: <PhoneOutlined /> },
              { key: '/admin/campaign-updates-config', label: 'Campaign Updates Config', icon: <NotificationOutlined /> },
              { key: '/admin/email-config',       label: 'Email Config',      icon: <MailOutlined /> },
              { key: '/admin/employee-jobs',      label: 'Employee Jobs',     icon: <SolutionOutlined /> },
              { key: '/admin/grade-weights',      label: 'Grade Weights',     icon: <TrophyOutlined /> },
              { key: '/admin/helpdesk-automations', label: 'Help Desk Automations', icon: <CustomerServiceOutlined /> },
              { key: '/admin/helpdesk-workflows', label: 'Help Desk Workflows', icon: <CustomerServiceOutlined /> },
              { key: '/admin/helpdesk-queues',    label: 'Help Desk Queues',  icon: <CustomerServiceOutlined /> },
              { key: '/admin/project-types',      label: 'Project Types',     icon: <ApartmentOutlined /> },
              { key: '/admin/teams',              label: 'Teams',             icon: <TeamOutlined /> },
            ] : []),
            ...(isAdmin || canEditUsers ? [{ key: '/admin', label: 'Users', icon: <TeamOutlined /> }] : []),
          ],
        },
        {
          key: 'admin-business',
          label: 'Business Config',
          icon: <ApartmentOutlined />,
          children: [
            ...(isAdmin || isQaAdmin ? [
              { key: '/admin/badges',             label: 'Badges',            icon: <TrophyOutlined /> },
              { key: '/admin/goals',              label: 'Campaign Goals',    icon: <TrophyOutlined /> },
              { key: '/admin/ccaas-statuses',     label: 'CCaaS Statuses',    icon: <ClockCircleOutlined /> },
              { key: '/admin/cheers-config',      label: 'Cheers for Peers',  icon: <LikeOutlined /> },
            ] : []),
            ...(canEditClients >= 1 ? [{ key: '/admin/clients', label: 'Clients', icon: <ContactsOutlined /> }] : []),
            ...(isAdmin || isQaAdmin ? [
              { key: '/admin/coaching-config',    label: 'Coaching Config',   icon: <BookOutlined /> },
              { key: '/admin/compliance-config',  label: 'Compliance Issues', icon: <SafetyOutlined /> },
              { key: '/admin/dbal-thresholds',    label: 'DBAL Thresholds',   icon: <ClockCircleOutlined /> },
              { key: '/admin/lock-dates',         label: 'Record Lock Dates', icon: <LockOutlined /> },
              { key: '/admin/skill-exclusions',   label: 'Skill Exclusions',  icon: <StopOutlined /> },
              { key: '/admin/skill-groups',       label: 'Skill Groups',      icon: <ApartmentOutlined /> },
            ] : []),
            ...(canWfmSkillsConfig ? [{ key: '/admin/wfm-skills', label: 'WFM Skills Config', icon: <IdcardOutlined /> }] : []),
            ...(isAdmin ? [{ key: '/admin/shift-department-eligibility', label: 'Shift Department Eligibility', icon: <SwapOutlined /> }] : []),
          ],
        },
        {
          key: 'admin-forms',
          label: 'Forms & Templates',
          icon: <FormOutlined />,
          children: [
            ...(isAdmin || isQaAdmin ? [{ key: '/admin/eval-criteria', label: 'Eval Criteria', icon: <AuditOutlined /> }] : []),
            ...(canConfigEvals ? [{ key: '/eval-forms', label: 'Eval Forms', icon: <FormOutlined /> }] : []),
            ...(isAdmin || isQaAdmin ? [{ key: '/admin/recap-templates', label: 'Recap Templates', icon: <FormOutlined /> }] : []),
          ],
        },
        {
          key: 'admin-uploads',
          label: 'File Uploads',
          icon: <UploadOutlined />,
          children: [
            ...(isAdmin || hasGrant(currentUser, 'bcbs-bill') ? [{ key: '/admin/bcbs-bill', label: 'BCBS Bill Upload', icon: <UploadOutlined /> }] : []),
            // CNE Stats Upload is off the menu, not removed. The monthly figures
            // are summed from the daily upload now, and nothing downstream reads
            // the monthly table any more, so uploading a monthly file would be
            // work that changes nothing. The route still resolves for anyone who
            // has the link, so old months can still be looked at.
            ...(isCneAuth || hasGrant(currentUser, 'cne-csat-upload') ? [{ key: '/admin/cne-csat-upload', label: 'CNE CSAT Upload', icon: <UploadOutlined /> }] : []),
            ...(isCneAuth || hasGrant(currentUser, 'cne-daily-upload') ? [{ key: '/admin/cne-daily-upload', label: 'CNE Daily Stats Upload', icon: <UploadOutlined /> }] : []),
            ...(isCneAuth || hasGrant(currentUser, 'cne-login-upload') ? [{ key: '/admin/cne-login-upload', label: 'CNE Login/Logout Upload', icon: <UploadOutlined /> }] : []),
            ...(isCneAuth || hasGrant(currentUser, 'cne-qa-upload') ? [{ key: '/admin/cne-qa-upload', label: 'CNE QA Stats Upload', icon: <UploadOutlined /> }] : []),
            ...(isAdmin || isQaAdmin || (currentUser?.JobID === 6 && currentUser?.TeamID === 1) || hasGrant(currentUser, 'conhome-upsell-import') ? [{ key: '/admin/conhome-upsell-import', label: 'ConHome Upsell Upload', icon: <UploadOutlined /> }] : []),
            ...(canViewCostRevenue
              ? [{ key: '/admin/payroll-gl', label: 'Payroll GL Upload', icon: <UploadOutlined /> }] : []),
            ...(isAdmin || hasGrant(currentUser, 'principal-bill') ? [{ key: '/admin/principal-bill', label: 'Principal Bill Upload', icon: <UploadOutlined /> }] : []),
            // Historical Campaign Updates (a one-off load).
            ...(isAdmin ? [{ key: '/admin/campaign-updates-upload', label: 'Campaign Updates Upload', icon: <UploadOutlined /> }] : []),
            ...(isAdmin || hasGrant(currentUser, 'stars-adherence-upload') ? [{ key: '/admin/stars-adherence-upload', label: 'Stars Adherence Level Upload', icon: <UploadOutlined /> }] : []),
          ],
        },
      ].filter((group) => group.children.length),
    }] : []),
  ];

  // A page's name for the Back chip when it sets none itself: the menu's name
  // for its path (or the nearest parent path), else a tidied path.
  const menuNames = {};
  const collectNames = (items) => (items || []).forEach((it) => {
    if (typeof it.key === 'string' && it.key.startsWith('/') && typeof it.label === 'string') menuNames[it.key] = it.label;
    collectNames(it.children);
  });
  collectNames(navItems);
  const pageName = (path) => {
    for (let p = path; p; p = p.slice(0, p.lastIndexOf('/'))) {
      if (menuNames[p]) return menuNames[p];
    }
    const seg = path.split('/').filter(Boolean)[0] || 'Home';
    return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // As the Harbor bar's profile menu: Profile (with the Theme page), then the other look.
  // Trying the redesign opens the Theme page on Classic, so the user picks a style there.
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => setProfileOpen(true),
    },
    {
      key: 'harbor',
      icon: <ExperimentOutlined />,
      label: 'Try the Harbor redesign',
      onClick: () => setProfileOpen(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign out',
      onClick: handleLogout,
    },
  ];

  const page = (
    <AntLayout className={harbor ? 'hb-root' : undefined} style={{ minHeight: '100vh' }}>
      {harbor ? (
        <HarborNav
          items={currentUser ? navItems : []}
          selectedKeys={selectedKeys}
          onNavigate={navigate}
          user={currentUser}
          dark={dark}
          onLogout={handleLogout}
          onProfile={() => setProfileOpen(true)}
          bell={currentUser && <AlertBell />}
          recent={currentUser && <RecentMenu />}
          create={currentUser && role !== 'client' && <QuickCreate />}
          extras={MOCK_MODE && <MockUserSwitcher />}
        />
      ) : (
      <Header style={{ display: 'flex', alignItems: 'center', padding: '0 24px', gap: 24, background: '#162d4e' }}>
        {/* The logo cropped to its blue card: the full image has a black
            frame, which showed as a dark line against the header. The login
            page still uses the full one. */}
        <img
          src="/sharborLogoHeader.png"
          alt="Harbor"
          style={{ height: 64, width: 'auto', flexShrink: 0, marginLeft: -24, display: 'block' }}
        />

        {currentUser && role !== 'client' && <QuickCreate />}

        <ClassicNav
          items={currentUser ? navItems : []}
          selectedKeys={selectedKeys}
          onNavigate={navigate}
        />

        {MOCK_MODE && <MockUserSwitcher />}
        {currentUser && <RecentMenu />}
        {currentUser && <AlertBell />}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.2 }}>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#e8682a', cursor: 'pointer' }} />
          </Dropdown>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Typography.Text style={{ color: '#fff', cursor: 'pointer' }}>
                {currentUser?.DisplayName || 'Loading…'}
              </Typography.Text>
            </Dropdown>
            {/* Light / dark, under the name where people see it: the one in
                use is bright, the other dim. */}
            <Space size={10} style={{ fontSize: 15 }}>
              {[
                { on: !dark, icon: <SunOutlined />, title: 'Light mode', value: false },
                { on: dark, icon: <MoonOutlined />, title: 'Dark mode', value: true },
              ].map((m) => (
                <Tooltip key={m.title} title={m.title}>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={m.title}
                    aria-pressed={m.on}
                    onClick={() => setDark(m.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setDark(m.value); }}
                    style={{ cursor: 'pointer', color: m.on ? '#fff' : 'rgba(255,255,255,0.45)' }}
                  >
                    {m.icon}
                  </span>
                </Tooltip>
              ))}
            </Space>
          </div>
        </div>
      </Header>
      )}

      {impersonateName && (
        <div style={{
          background: '#faad14', color: '#000',
          padding: '6px 24px', display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span>Impersonating: <strong>{impersonateName}</strong> — you are viewing the app as this user</span>
          <Button size="small" onClick={stopImpersonating}>Stop Impersonating</Button>
        </div>
      )}
      {newVersion && (
        <div style={{
          background: '#1677ff', color: '#fff',
          padding: '6px 24px', display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span>Harbor has been updated. Refresh to get the latest version.</span>
          <Button size="small" onClick={() => window.location.reload()}>Refresh</Button>
        </div>
      )}
      <Content style={{ padding: '24px', ...(harbor && { paddingTop: 44 }), background: harbor ? 'transparent' : (dark ? '#0d1117' : '#f0f4f8') }}>
        {deniedNotice && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message="You don't have access to that page, so you've been taken to My Summary. If you need access, contact your Supervisor." />
        )}
        {sessionNotice && !deniedNotice && (
          <Alert type="warning" showIcon closable style={{ marginBottom: 16 }} onClose={() => setSessionNotice(false)}
            message="Your session expired, so you've been returned to the start. Please try again." />
        )}
        <BackChip />
        {harbor && HARBOR_PAGES.includes(location.pathname)
          ? <div className="hb-page">{children}</div>
          : children}
      </Content>
      <DailyRecapModal currentUser={currentUser} />
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} user={currentUser}
        dark={dark} setDark={setDark} navStyle={navStyle} setNavStyle={setNavStyle} />
    </AntLayout>
  );
  // The Harbor look recolours everything inside the layout, pop-ups included. Always wrapped,
  // so switching looks doesn't remount the page.
  // The navigation trail wraps header and page alike: the Back chip is drawn
  // in the page, Recent in the top bar.
  return (
    <HarborTheme dark={dark} on={harbor}>
      <NavTrailProvider defaultLabel={pageName} userId={currentUser?.ID}>{page}</NavTrailProvider>
    </HarborTheme>
  );
}
